/**
 * homeShared — small shared primitives for the living-archive homepage.
 * Matches the established Archive visual language (dark, mono labels, red rule).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export const resolveImg = (src) => {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return src.startsWith("/") ? `${base}${src}` : src;
};

export const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/** Animated count-up that respects prefers-reduced-motion. */
export const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  const ran = useRef(false);
  useEffect(() => {
    if (typeof target !== "number") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || ran.current) { setVal(target); return; }
    ran.current = true;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return typeof target === "number" ? val : null;
};

export const SectionHeader = ({ file, title, subtitle, action, actionLabel, testid }) => (
  <div className="mb-6 md:mb-12 flex items-end justify-between flex-wrap gap-3 md:gap-4" data-testid={testid}>
    <div>
      <div className="flex items-center gap-3 mb-2 md:mb-3">
        <span className="h-px w-10" style={{ background: "#D92D20" }} />
        <div className="font-mono text-[9px] uppercase tracking-[0.5em] text-[#D92D20]">{file}</div>
      </div>
      <h2 className="font-display text-[26px] sm:text-3xl md:text-4xl lg:text-[44px] text-[#E4DFD3] leading-[1.05] tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-2 md:mt-3 text-[12.5px] md:text-[14px] text-[#8A8377] max-w-2xl leading-relaxed">{subtitle}</p>
      )}
    </div>
    {action && (
      <Link
        to={action}
        data-testid={testid ? `${testid}-action` : undefined}
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em]
                   text-[#A39E93] hover:text-[#E4DFD3] transition-colors border-b border-[#454038] hover:border-[#E4DFD3] pb-1"
      >
        {actionLabel || "View all"} <ArrowRight size={12} />
      </Link>
    )}
  </div>
);

/** Horizontal snap rail with edge fades + swipe affordance on mobile. */
export const HRail = ({ children, testid, itemClass = "" }) => {
  const ref = useRef(null);
  const [canRight, setCanRight] = useState(true);
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 12);
  };
  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={onScroll}
        data-testid={testid}
        className="flex gap-3 md:gap-4 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory
                   [scrollbar-width:thin] [scrollbar-color:#2D2A26_transparent]"
        style={{ scrollPaddingLeft: "1rem" }}
      >
        {React.Children.map(children, (c) => (
          <div className={`snap-start shrink-0 ${itemClass}`}>{c}</div>
        ))}
      </div>
      {canRight && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 bottom-3 w-10 md:w-20"
          style={{ background: "linear-gradient(90deg, transparent, #0B0A0D 85%)" }}
        />
      )}
    </div>
  );
};

/** Compact list row with thumbnail — used across What's Happening / expansions. */
export const MiniRow = ({ dossier, index, testid }) => (
  <Link
    to={`/topic/${dossier.slug}`}
    data-testid={testid}
    className="flex items-center gap-3 py-2.5 border-b last:border-b-0 group hover:bg-[#131015] transition-colors px-2 -mx-2"
    style={{ borderColor: "#2D2A26" }}
  >
    {typeof index === "number" && (
      <span className="font-mono text-[10px] shrink-0 text-[#454038] group-hover:text-[#E4DFD3] transition-colors w-5">
        {String(index + 1).padStart(2, "0")}
      </span>
    )}
    <div
      className="w-11 h-11 shrink-0 border overflow-hidden"
      style={{ borderColor: "#2D2A26", background: "#131015" }}
    >
      {dossier.image_url ? (
        <img
          src={resolveImg(dossier.thumb_url || dossier.image_url)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-display text-[16px] text-[#454038]">
          {(dossier.title || "?").charAt(0)}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] leading-snug text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors line-clamp-2">
        {dossier.title}
      </div>
      <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.3em] text-[#8A8377]">
        {dossier.category || "CLASSIFIED"}
      </div>
    </div>
    <ArrowUpRight size={13} className="text-[#454038] group-hover:text-[#D92D20] shrink-0 transition-colors" />
  </Link>
);
